import { Component, Prop, Vue } from 'vue-property-decorator'
import ConfigurationOntologies from '@/modules/configurationModule/shared/components/configuration-ontologies/configuration-ontologies.vue'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { ConfigurationProtocolResponse } from '@/modules/configurationModule/detail/local-assays/protocols/services/configuration-protocol/configuration-protocol.dto'
import {
    ConfigurationLocalAssaysProtocolsModalOntologiesFormState,
    initialConfigurationLocalAssaysProtocolsModalOntologiesFormState
} from '@/modules/configurationModule/detail/local-assays/protocols/components/modal-ontologies-form/configuration-local-assays-protocols-modal-ontologies-form.model'
import {
    ConfigurationProtocolOntologyCreateRequest,
    ConfigurationProtocolOntologyResponse
} from '@/modules/configurationModule/detail/local-assays/protocols/services/configuration-protocol-ontology/configuration-protocol-ontology.dto'
import { configurationProtocolOntologyService } from '@/modules/configurationModule/detail/local-assays/protocols/services/configuration-protocol-ontology/configuration-protocol-ontology.service'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { ConfigurationOntologiesState } from '@/modules/configurationModule/shared/components/configuration-ontologies/configuration-ontologies.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { GridUtils } from '@/shared/util/grid-utils'

@Component({
    components: {
        ConfigurationOntologies
    }
})
export default class ConfigurationLocalAssaysProtocolsModalOntologiesForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly protocol!: ConfigurationProtocolResponse

    state: ConfigurationLocalAssaysProtocolsModalOntologiesFormState
    constructor() {
        super()
        this.state = initialConfigurationLocalAssaysProtocolsModalOntologiesFormState()
    }

    private async _loadProtocolOntologies(): Promise<void> {
        const protocolOntologies = await this._getProtocolOntologies()
        this.state.ontologies = protocolOntologies.map(ontology => ({
            id: ontology.id,
            sampleType: ontology.sampleType,
            topographicCode: ontology.topographicCode,
            mostLikelyMorphologicCode: ontology.mostLikelyMorphologicCode,
            procedure: ontology.procedure,
            protocolId: ontology.protocol.id
        }))
    }

    async mounted() {
        await this._loadProtocolOntologies()
    }

    /// SERVICES CALLS ///

    private async _createProtocolOntology(createRequest: ConfigurationProtocolOntologyCreateRequest): Promise<ConfigurationProtocolOntologyResponse | null> {
        const response = await this.$_toCallUnique(configurationProtocolOntologyService.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _getProtocolOntologies(): Promise<ConfigurationProtocolOntologyResponse[]> {
        const filterModel: CommonFilterModel = {
            'protocol.prtId': GridUtils.getNumberFilterModel('equals', this.protocol.id)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallUnique(configurationProtocolOntologyService.paginator(filterModel, params, this.state.laboratoryId))
        if (response) {
            return response.content
        }
        return []
    }

    private async _deleteProtocolOntology(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(configurationProtocolOntologyService.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
                }
                return response
            }
        }
        return null
    }

    /// FORM OPERATIONS ///

    async createOperation(state: ConfigurationOntologiesState): Promise<void> {
        const createRequest: ConfigurationProtocolOntologyCreateRequest = {
            protocolId: this.protocol.id,
            sampleTypeId: state.sampleType!.id,
            mostLikelyMorphologicCodeId: state.mostLikelyMorphologicCode?.id,
            procedureId: state.procedure?.id,
            topographicCodeId: state.topographicCode?.id
        }
        if (await this._createProtocolOntology(createRequest)) {
            await this._loadProtocolOntologies()
        }
    }

    async deleteOperation(protocolOntologyId: number): Promise<void> {
        if (protocolOntologyId) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: [protocolOntologyId]
            }
            if (await this._deleteProtocolOntology(requestDelete)) {
                await this._loadProtocolOntologies()
            }
        }
    }
}
